<template>
  <div>
    <b-card :key="renderKey">
      <b-row class="d-flex justify-content-between">
        <b-col cols="12" md="6" class="d-flex align-items-center">
          <b-button
            variant="outline"
            class="mr-2 text font-weight-bolder"
            :style="{ backgroundColor: getColorFromProfileName(activeProfile) }"
            style="color: white"
          >
            {{ activeProfile }}</b-button
          >
          <div>
            <h5 class="mb-0">Sports Betting</h5>
            <small class="text-muted">
              Configure betting limits for sports betting events
            </small>
          </div>
        </b-col>

        <b-col cols="12" md="6" class="d-flex align-items-end flex-column">
          <div class="d-flex justify-content-end align-items-center mt-1">
            <b-button
              @click="handleReset"
              size="sm"
              variant="outline-secondary"
              class="mr-2"
            >
              Reset
            </b-button>
            <b-button size="sm" variant="primary">
              Save Configuration
            </b-button>
          </div>
          <div class="mt-1">
            <a
              href="/template_config_sport.csv"
              download="template_config_sport.csv"
              class="download-button mr-1"
            >
              <feather-icon icon="ArrowDownIcon" />
              Sample CSV
            </a>
            <input
              ref="fileInput"
              type="file"
              accept=".csv"
              hidden
              @change="handleFileUpload"
            />
            <b-button size="sm" variant="outline-primary" @click="onClickInput">
              <feather-icon icon="UploadIcon" />

              Upload CSV
            </b-button>
          </div>
        </b-col>
      </b-row>

      <b-table :items="rows" :fields="fields" responsive bordered class="mt-1 min-w-[8000px]" >
        <template #head(league)>
          <div class="header-cell bg-light-cyan">Sports/Leagues</div>
        </template>
        <template #head(cashAmount)>
          <div class="header-cell bg-light-blue">
            CASH AMOUNT<br />(MAX STAKE)
          </div>
        </template>
        <template #head(bonusAmount)>
          <div class="header-cell bg-light-green">BONUS<br />(MAX STAKE)</div>
        </template>
        <template #head(cashLiability)>
          <div class="header-cell bg-light-purple">
            CASH<br />AMOUNT (LIABILITY)
          </div>
        </template>
        <template #head(bonusLiability)>
          <div class="header-cell bg-light-yellow">
            BONUS<br />AMOUNT (LIABILITY)
          </div>
        </template>
        <template #head(accept)>
          <div class="header-cell">ACCEPT</div>
        </template>

        <template #cell(league)="data">
          <div class="d-flex align-items-center">
            <b-button
              v-if="data.item.type !== 'other'"
              size="sm"
              class="mr-1"
              style="padding: 2px 4px; min-width: 45px"
              @click="handleAddTier(data.item)"
              >+ Tier</b-button
            >
            <strong
              >{{ data.item.league }}
              {{
                checkShowTierLabel(data.item) ? `T${data.item.tier}` : ""
              }}</strong
            >
          </div>
        </template>

        <template v-for="f in numericFields" v-slot:[`cell(${f})`]="data">
          <b-form-input
            size="sm"
            type="number"
            class="input-cell"
            v-model="data.item[f]"
            :key="`${data.index}-${f}`"
          />
        </template>

        <template #cell(accept)="data">
          <div class="d-flex align-items-center" style="min-width: 90px">
            <b-form-select
              :options="['Auto', 'Manual']"
              v-model="data.item.accept"
              size="sm"
            />
          </div>
        </template>
      </b-table>
      <div class="d-flex justify-content-end align-items-center mt-1">
        <b-button
          @click="handleReset"
          size="sm"
          variant="outline-secondary"
          class="mr-2"
        >
          Reset
        </b-button>
        <b-button size="sm" variant="primary" @click="handleSaveConfig">
          Save Configuration
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BTable,
  BFormInput,
  BFormSelect,
  BBadge,
  BButton,
  BRow,
  BCol,
} from "bootstrap-vue";
import { mapState, mapActions } from "vuex";
import { v4 as uuidv4 } from "uuid";
import useToastHandler from "@/services/toastHandler";
import { parse } from "csv-parse";

export default {
  components: {
    BCard,
    BTable,
    BFormInput,
    BFormSelect,
    BBadge,
    BButton,
    BRow,
    BCol,
  },
  data() {
    const fields = [
      { key: "league", label: "SPORTS/LEAGUES" },
      { key: "cashAmount" },
      { key: "bonusAmount" },
      { key: "cashLiability" },
      { key: "bonusLiability" },
      { key: "accept" },
    ];

    return {
      fields,
      rows: [],
      numericFields: [
        "cashAmount",
        "bonusAmount",
        "cashLiability",
        "bonusLiability",
      ],
      initialData: {
        cashAmount: 0,
        bonusAmount: 0,
        cashLiability: 0,
        bonusLiability: 0,
        tier: 1,
        accept: "Auto",
      },
      sports: [],
      initialConfigSport: [],
      renderKey: 0,
    };
  },
  async created() {
    await this.fetchSports();
    await this.fetchOriginalData();
  },
  watch: {
    async activeProfile() {
      this.initialConfigSport = [];
      this.renderKey += 1;
      await this.fetchSports();
      await this.fetchOriginalData();
    },
  },
  computed: {
    ...mapState("bettingConfig/list", ["activeProfile", "loadingList"]),
  },
  methods: {
    ...mapActions({
      getListSport: "sport/list/getListSport",
      upsetProfileSportConfig: "bettingConfig/list/upsetProfileSportConfig",
      getConfigSportList: "bettingConfig/list/getConfigSportList",
    }),
    async fetchOriginalData() {
      const res = await this.getConfigSportList(this.activeProfile);
      if (!res?.length) return;
      this.rows = this.rows.map((row) => {
        const bonusConfig = res.find(
          (item) => item.sportId === row.id && item.currencyMethod === "bonus"
        );
        const cashConfig = res.find(
          (item) => item.sportId === row.id && item.currencyMethod === "cash"
        );
        return {
          ...row,
          cashAmount: cashConfig ? cashConfig.maxStake : row.cashAmount,
          cashLiability: cashConfig
            ? cashConfig.maxLiability
            : row.cashLiability,
          bonusAmount: bonusConfig ? bonusConfig.maxStake : row.bonusAmount,
          bonusLiability: bonusConfig
            ? bonusConfig.maxLiability
            : row.bonusLiability,
          accept:
            (cashConfig && cashConfig.autoAccept) ||
            (bonusConfig && bonusConfig.autoAccept)
              ? "Auto"
              : "Manual",
        };
      });
      const filterTieredData = res.filter((item) => item.tier > 1);
      if (filterTieredData.length > 0) {
        filterTieredData.forEach((item) => {
          const existingRow = this.rows
            .filter((r) => r.id === item.sportId)
            .at(-1);
          if (existingRow) {
            if (existingRow.tier !== item.tier) {
              const newTierItem = {
                league: existingRow.league,
                id: existingRow.id,
                uuid: uuidv4(),
                type: existingRow.type,
                cashAmount:
                  item.currencyMethod === "cash"
                    ? item.maxStake
                    : existingRow.cashAmount,
                bonusAmount:
                  item.currencyMethod === "bonus"
                    ? item.maxStake
                    : existingRow.bonusAmount,
                cashLiability:
                  item.currencyMethod === "cash"
                    ? item.maxLiability
                    : existingRow.cashLiability,
                bonusLiability:
                  item.currencyMethod === "bonus"
                    ? item.maxLiability
                    : existingRow.bonusLiability,
                tier: item.tier,
                accept: item.autoAccept ? "Auto" : "Manual",
              };
              const findIndex = this.rows.findIndex(
                (r) => r.id === item.sportId
              );
              this.rows.splice(findIndex + 1, 0, newTierItem);
            } else {
              // Update existing row
              if (item.currencyMethod === "cash") {
                existingRow.cashAmount = item.maxStake;
                existingRow.cashLiability = item.maxLiability;
              } else if (item.currencyMethod === "bonus") {
                existingRow.bonusAmount = item.maxStake;
                existingRow.bonusLiability = item.maxLiability;
              }
              existingRow.accept = item.autoAccept ? "Auto" : "Manual";
            }
          }
        });
      }

      this.initialConfigSport = JSON.parse(JSON.stringify(this.rows));
    },
    async fetchSports() {
      const data = await this.getListSport({
        limit: 100,
        status: "active",
      });
      if (!data && data.data.length <= 0)
        return this.showToastError(false, "Error fetching sports list");
      this.sports = data.data.map((sport) => {
        return {
          label: sport.name,
          id: sport.id,
        };
      });

      this.rows = [
        ...this.sports.map((sport) => {
          return {
            league: sport.label,
            id: sport.id,
            uuid: uuidv4(),
            type: "sport",
            ...this.initialData,
          };
        }),
        {
          league: "Any Other Sport",
          id: "any-other-sport",
          uuid: uuidv4(),
          type: "other",
          ...this.initialData,
        },
        {
          league: "SGM",
          id: "sgm",
          uuid: uuidv4(),
          type: "other",
          ...this.initialData,
        },
        {
          league: "Cross Multi ANY Sport and racing",
          id: "cross-multi",
          uuid: uuidv4(),
          type: "other",
          ...this.initialData,
        },
      ];
    },
    handleAddTier(item) {
      const maxTier = this.rows
        .filter((r) => r.id === item.id)
        .reduce((max, r) => (r.tier > max ? r.tier : max), 0);

      const newTier = maxTier + 1;

      const newTierItem = {
        league: item.league,
        id: item.id,
        uuid: uuidv4(),
        type: item.type,
        ...JSON.parse(JSON.stringify(this.initialData)),
        tier: newTier,
      };

      let lastIndex = -1;

      for (let i = this.rows.length - 1; i >= 0; i--) {
        if (this.rows[i].id === item.id) {
          lastIndex = i;
          break;
        }
      }

      if (lastIndex !== -1) {
        this.rows.splice(lastIndex + 1, 0, newTierItem);
      } else {
        this.rows.push(newTierItem);
      }
    },
    checkShowTierLabel(row) {
      const checkList = this.rows.filter((r) => r.id === row.id);
      return checkList.length > 1;
    },
    async handleSaveConfig() {
      const list = [];
      this.rows.forEach((item) => {
        const configItemCash = {
          sportId: item.id,
          tier: item.tier,
          currencyMethod: "cash",
          maxStake: Number(item.cashAmount),
          maxLiability: Number(item.cashLiability),
          autoAccept: item.accept === "Auto",
        };
        list.push(configItemCash);
        const configItemBonus = {
          sportId: item.id,
          tier: item.tier,
          currencyMethod: "bonus",
          maxStake: Number(item.bonusAmount),
          maxLiability: Number(item.bonusLiability),
          autoAccept: item.accept === "Auto",
        };
        list.push(configItemBonus);
      });
      const res = await this.upsetProfileSportConfig({
        profileTypeId: this.activeProfile,
        configs: list,
      });
      if (res.data) {
        this.showToastSuccess(true, "Save configuration successfully!");
      } else {
        this.showToastError(false, "Error saving configuration!");
        this.rows = JSON.parse(JSON.stringify(this.initialConfigSport));
      }
    },
    getColorFromProfileName(name) {
      const colorList = [
        "#5A6A7A",
        "#F48FB1",
        "#8BC34A",
        "#F44336",
        "#FFEB3B",
        "#FF9800",
        "#03A9F4",
        "#4CAF50",
      ];
      if (!name || colorList.length === 0) {
        return "#CCCCCC";
      }
      let hash = 0;
      const cleanName = name.toLowerCase();

      for (let i = 0; i < cleanName.length; i++) {
        hash += cleanName.charCodeAt(i);
      }
      const colorIndex = hash % colorList.length;

      return colorList[colorIndex];
    },
    handleReset() {
      this.rows = JSON.parse(JSON.stringify(this.initialConfigSport));
    },
    onClickInput() {
      const fileInput = this.$refs.fileInput;
      fileInput.click();
    },
    _resetInputFile() {
      this.$refs.fileInput.value = null;
    },
    handleFileUpload(event) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.parseCSV(reader.result);
      };
      reader.readAsText(file);
      this._resetInputFile();
    },
    parseCSV(csvData) {
      parse(csvData, { delimiter: "," }, (err, data) => {
        if (err) {
          console.error(err);
          return;
        }
        data.shift();

        this.rows = this.rows.map((row, index) => {
          return {
              ...row,
              cashAmount: (data[index][1]) || row.cashAmount,
              bonusAmount: (data[index][2]) || row.bonusAmount,
              cashLiability: (data[index][3]) || row.cashLiability,
              bonusLiability: (data[index][4]) || row.bonusLiability,
              accept:
                data[index][5] && (data[index][5].toLowerCase() === "auto" ? "Auto" : "Manual"),
            };
        });
      });
    },
  },
  setup() {
    const { showToastSuccess, showToastError } = useToastHandler();

    return {
      showToastSuccess,
      showToastError,
    };
  },
};
</script>

<style lang="scss">
[dir] .table th {
  padding: 0 !important;
}
.header-cell {
  text-align: center;
  font-weight: bold;
  display: flex;
  padding: 8px;
  height: 60px;
  align-items: center;
  justify-content: center;
}
.bg-light-cyan {
  background: #cefafe !important;
}
.bg-light-blue {
  background: #d6f0ff;
}
.bg-light-green {
  background: #e5ffe5;
}
.bg-light-purple {
  background: #f2e5ff;
}
.bg-light-yellow {
  background: #fff8d6;
}
.bg-light-cyan {
  background: #e6faff;
}
.bg-light-pink {
  background: #ffe5ec;
}

.input-cell {
  text-align: center;
  border-radius: 6px;
  font-size: 14px;
}

.download-button {
  font-weight: 500;
  font-size: 13px;
  border: 1px solid #2b65a5;
  border-radius: 5px;
  padding: 6px 12px;
}
</style>